import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState, memo } from 'react'
import type { MasterRunbook } from './api'
import { TranslatableText } from '@/components/ui/TranslatableText'

interface MasterRunbookActionsProps {
    masterRunbook: MasterRunbook
    onEdit: (masterRunbook: MasterRunbook) => void
    onDelete: (masterRunbook: MasterRunbook) => void
    onView: (masterRunbook: MasterRunbook) => void
}

const MasterRunbookActions = memo(function MasterRunbookActions({
    masterRunbook,
    onEdit,
    onDelete,
    onView,
}: MasterRunbookActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">{t('common.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onView(masterRunbook)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('common.view')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onEdit(masterRunbook)
                            setOpen(false)
                        }}
                    >
                        <Edit className="mr-2 h-4 w-4" />
                        {t('common.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onDelete(masterRunbook)
                            setOpen(false)
                        }}
                        className="text-destructive"
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('common.delete')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
})

export function createMasterRunbookColumns(
    onEdit: (masterRunbook: MasterRunbook) => void,
    onDelete: (masterRunbook: MasterRunbook) => void,
    onView: (masterRunbook: MasterRunbook) => void,
    t: (key: string) => string
): ColumnDef<MasterRunbook>[] {
    return [
        {
            accessorKey: 'description',
            header: t('masterRunbooks.description'),
            cell: ({ row }) => {
                const description = row.getValue('description') as string
                return <div className="font-medium">{description ? <TranslatableText text={description} /> : '-'}</div>
            },
        },
        {
            accessorKey: 'information',
            header: t('masterRunbooks.information'),
            cell: ({ row }) => {
                const information = row.getValue('information') as string
                if (!information) {
                    return <div className="text-sm text-muted-foreground">-</div>
                }
                return (
                    <div 
                        className="max-w-[400px] text-sm text-muted-foreground line-clamp-2 prose prose-sm"
                        dangerouslySetInnerHTML={{ __html: information }}
                    />
                )
            },
        },
        {
            id: 'actions',
            header: () => <div className="text-right w-full">{t('common.actions')}</div>,
            cell: ({ row }) => {
                return (
                    <div className="flex justify-end">
                        <MasterRunbookActions
                            masterRunbook={row.original}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onView={onView}
                        />
                    </div>
                )
            },
        },
    ]
}
